import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, Typography, Alert,
  FormControlLabel, Checkbox, Chip, Box, Paper,
} from '@mui/material';
import { CREATE_VENDOR_SELECTION_MUTATION, UPDATE_VENDOR_SELECTION_MUTATION } from '../graphql/queries';

// ── Status display (shared by the family's and the vendor's pages) ──────────
export const BOOKING_STATUS = {
  requested: { label: 'Awaiting vendor confirmation', vendorLabel: 'New request', color: 'warning' },
  confirmed: { label: 'Confirmed', vendorLabel: 'Confirmed', color: 'success' },
  declined:  { label: 'Declined by vendor', vendorLabel: 'Declined', color: 'error' },
  cancelled: { label: 'Cancelled', vendorLabel: 'Cancelled by family', color: 'default' },
  completed: { label: 'Completed', vendorLabel: 'Completed', color: 'info' },
};

export function BookingStatusChip({ status, forVendor = false }) {
  const cfg = BOOKING_STATUS[status] || { label: status, color: 'default' };
  return <Chip size="small" color={cfg.color} variant={cfg.color === 'default' ? 'outlined' : 'filled'} label={forVendor ? cfg.vendorLabel : cfg.label} />;
}

export const fmtBookingDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

export function countdown(days) {
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days > 1) return `In ${days} days`;
  if (days === -1) return 'Yesterday';
  return `${-days} days ago`;
}

const inr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
const today = () => new Date().toISOString().slice(0, 10);

// What each service asks for when booking: the time label and, where it matters, the quantity
// (`required` when the price is charged per unit, e.g. per plate)
const BOOKING_FIELDS = {
  catering:       { time: 'Serving time', quantity: 'Number of plates', unit: 'plates', required: true },
  venue:          { time: 'Start time', quantity: 'Expected guests', unit: 'guests' },
  seating:        { time: 'Set up by', quantity: 'Number of guests', unit: 'guests', required: true },
  accommodation:  { time: 'Check-in time', quantity: 'Number of rooms', unit: 'rooms', required: true },
  transportation: { time: 'Pickup time', quantity: 'Number of vehicles', unit: 'vehicles', required: true },
  beauty:         { time: 'Appointment time', quantity: 'Number of people', unit: 'people', required: true },
  gifts:          { time: 'Delivery time', quantity: 'Number of gifts', unit: 'gifts', required: true },
  vip_escort:     { time: 'Escort from', quantity: 'Number of VIP guests', unit: 'VIP guests', required: true },
  parking:        { time: 'Parking opens', quantity: 'Expected vehicles', unit: 'vehicles' },
  staffing:       { time: 'Reporting time', quantity: 'Number of staff', unit: 'staff', required: true },
  invitation:     { time: 'Delivery time', quantity: 'Number of invitations', unit: 'invitations', required: true },
  coordination:   { time: 'Start time', quantity: 'Expected guests', unit: 'guests' },
  photography:    { time: 'Coverage starts' },
  decoration:     { time: 'Ready by' },
  entertainment:  { time: 'Performance time' },
  rituals:        { time: 'Muhurtham time' },
  astrology:      { time: 'Consultation time' },
  honeymoon:      { time: 'Departure time', quantity: 'Number of travellers', unit: 'travellers' },
};
const fieldsFor = (category) => BOOKING_FIELDS[category] || { time: 'Time', quantity: 'Quantity', unit: '' };
// "600 plates" for a booking (vendor and family lists)
export const quantityText = (category, quantity) => (quantity ? `${quantity} ${fieldsFor(category).unit || 'qty'}` : '');

// Per-unit prices (per plate, per room...) are multiplied by the quantity; others are a flat price
const PER_UNIT = /\bper (plate|guest|person|head|gift|room|vehicle|trip|set|tray|piece|item|couple)/i;
const estimate = (price, priceUnit, quantity) => (
  price == null ? null : Number(price) * (quantity && PER_UNIT.test(priceUnit || '') ? Number(quantity) : 1)
);

const blankDetails = () => ({ requiredDate: '', requiredTime: '', quantity: '', notes: '' });

// ── Book a vendor: tick one or more packages, each with its own date, time and quantity ──
// Each ticked package becomes its own booking request (the vendor confirms each); none ticked
// sends one request to discuss with the vendor.
export function BookVendorDialog({ open, onClose, offering, labels, initialPackageId, forFamily, onBooked }) {
  const [details, setDetails] = useState({}); // packageId ('' = no package) -> { requiredDate, requiredTime, quantity, notes }
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // The vendor's cancellation & refund terms must be read and accepted before booking
  const [termsOk, setTermsOk] = useState(false);
  const [book] = useMutation(CREATE_VENDOR_SELECTION_MUTATION);

  useEffect(() => {
    if (open) {
      setDetails(initialPackageId ? { [initialPackageId]: blankDetails() } : {});
      setError('');
      setTermsOk(false);
    }
  }, [open, initialPackageId]);

  if (!offering) return null;
  const f = fieldsFor(offering.category);
  const packages = offering.packages || [];
  const picked = packages.filter(p => details[p.id]);
  // With no package ticked, one request with its own details
  const rows = picked.length ? picked : [{ id: '', name: null }];
  const rowDetails = (id) => details[id] || blankDetails();
  const setRow = (id, k) => e => setDetails(d => ({ ...d, [id]: { ...(d[id] || blankDetails()), [k]: e.target.value } }));
  const togglePackage = (id) => setDetails(d => {
    const next = { ...d };
    if (next[id]) { delete next[id]; return next; }
    // A newly ticked package starts with the last one's date and time
    const last = picked.length ? d[picked[picked.length - 1].id] : d[''];
    next[id] = { ...blankDetails(), requiredDate: last?.requiredDate || '', requiredTime: last?.requiredTime || '' };
    delete next[''];
    return next;
  });

  const rowProblem = (row) => {
    const r = rowDetails(row.id);
    if (!r.requiredDate) return 'date';
    if (f.required && f.quantity && !(Number(r.quantity) > 0)) return 'quantity';
    return null;
  };
  const incomplete = rows.some(rowProblem);
  const total = picked.reduce((n, p) => n + (estimate(p.price, p.priceUnit, rowDetails(p.id).quantity) || 0), 0);

  const submit = async () => {
    setError('');
    setBusy(true);
    const booked = [];
    const failed = [];
    for (const row of rows) { // the vendor's package order
      const r = rowDetails(row.id);
      try {
        const { data } = await book({
          variables: {
            input: {
              offeringId: offering.id,
              packageId: row.id || null,
              requiredDate: r.requiredDate,
              requiredTime: r.requiredTime || null,
              quantity: r.quantity ? Number(r.quantity) : null,
              notes: r.notes || null,
              acceptCancellationTerms: termsOk,
            },
          },
        });
        booked.push(data.createVendorSelection);
      } catch (e) { failed.push(`${row.name ? `${row.name}: ` : ''}${e.message}`); }
    }
    setBusy(false);
    if (failed.length) {
      // Keep the dialog open on the packages that did not go through
      const done = new Set(booked.map(b => b.packageId || ''));
      setDetails(d => Object.fromEntries(Object.entries(d).filter(([id]) => !done.has(id))));
      setError(`${booked.length ? `${booked.length} booked. ` : ''}${failed.join(' ')}`);
      return;
    }
    onBooked?.(booked[0], booked);
  };

  // Date, time, quantity and a note for one request
  const detailFields = (row) => {
    const r = rowDetails(row.id);
    const pkg = packages.find(p => p.id === row.id);
    const est = pkg && r.quantity && PER_UNIT.test(pkg.priceUnit || '') ? estimate(pkg.price, pkg.priceUnit, r.quantity) : null;
    return (
      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField label="Date *" type="date" value={r.requiredDate} onChange={setRow(row.id, 'requiredDate')}
            fullWidth size="small" InputLabelProps={{ shrink: true }} inputProps={{ min: today() }} />
          <TextField label={f.time} type="time" value={r.requiredTime} onChange={setRow(row.id, 'requiredTime')}
            fullWidth size="small" InputLabelProps={{ shrink: true }} />
          {f.quantity && (
            <TextField label={`${f.quantity}${f.required ? ' *' : ''}`} type="number" value={r.quantity} onChange={setRow(row.id, 'quantity')}
              fullWidth size="small" inputProps={{ min: 1 }}
              helperText={est != null ? `≈ ${inr(est)} (${inr(pkg.price)} ${pkg.priceUnit})` : ' '} />
          )}
        </Stack>
        <TextField label={row.name ? `Notes for "${row.name}"` : 'Notes for the vendor'} value={r.notes} onChange={setRow(row.id, 'notes')}
          fullWidth size="small" placeholder="e.g. ceremony, venue, special requests" />
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Book {offering.businessName}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography fontWeight={600}>{offering.title}</Typography>
            <Typography variant="body2" color="text.secondary">{offering.categoryName} · {offering.city}</Typography>
            {forFamily && <Alert severity="info" sx={{ mt: 1 }}>Booking for <strong>{forFamily}</strong></Alert>}
          </Box>
          {packages.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Tick one or more {(labels?.packages || 'packages').toLowerCase()}. Each gets its own date{f.quantity ? `, time and ${f.quantity.toLowerCase()}` : ' and time'},
              and is sent to the vendor as its own booking request.
            </Typography>
          )}
          {packages.map(p => {
            const on = Boolean(details[p.id]);
            return (
              <Paper key={p.id} variant="outlined" sx={{ p: 1.5, borderColor: on ? 'primary.main' : 'divider' }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={on} onChange={() => togglePackage(p.id)} />}
                  label={<span><strong>{p.name}</strong> — {inr(p.price)} {p.priceUnit} · {p.items.length} {(labels?.items || 'items').toLowerCase()}</span>} />
                {on && <Box mt={1}>{detailFields(p)}</Box>}
              </Paper>
            );
          })}
          {!picked.length && (
            <Box>
              {packages.length > 0 && (
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  Nothing ticked: send one request to discuss the details with the vendor.
                </Typography>
              )}
              {detailFields({ id: '', name: null })}
            </Box>
          )}
          {picked.length > 0 && (
            <Typography variant="body2" fontWeight={600}>
              {picked.length} selected · estimated {inr(total)}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            The vendor is notified and will confirm or decline each request. You'll both get reminders 30, 7 and 1 day(s) before the date and on the day.
          </Typography>
          {offering.cancellationPolicy && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fffde7', borderColor: '#f9a825' }}>
              <Typography variant="subtitle2" fontWeight={700}>Cancellation & refund terms of {offering.businessName}</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line', my: 0.5 }}>{offering.cancellationPolicy}</Typography>
              <FormControlLabel
                control={<Checkbox size="small" checked={termsOk} onChange={(e) => setTermsOk(e.target.checked)} />}
                label={<Typography variant="body2">I have read and accept these terms and the <a href="/legal/refunds" target="_blank" rel="noopener noreferrer">Refunds &amp; Cancellations Policy</a>.</Typography>}
              />
            </Paper>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        {incomplete && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
            Add a date{f.required && f.quantity ? ` and ${f.quantity.toLowerCase()}` : ''} for each {picked.length ? 'selection' : 'request'}.
          </Typography>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={incomplete || busy || (Boolean(offering.cancellationPolicy) && !termsOk)}>
          {rows.length > 1 ? `Send ${rows.length} booking requests` : 'Send booking request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Change a booking's date or details (family) ────────────────────────────
export function EditBookingDialog({ open, onClose, booking, onSaved }) {
  const [form, setForm] = useState({});
  const [error, setError] = useState('');
  const [save, { loading }] = useMutation(UPDATE_VENDOR_SELECTION_MUTATION);
  useEffect(() => {
    if (booking) setForm({ requiredDate: booking.requiredDate, requiredTime: booking.requiredTime || '', quantity: booking.quantity || '', notes: booking.notes || '' });
    setError('');
  }, [booking]);
  if (!booking) return null;
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const dateChanged = form.requiredDate !== booking.requiredDate;
  const submit = async () => {
    try {
      await save({ variables: { id: booking.id, input: {
        requiredDate: form.requiredDate, requiredTime: form.requiredTime || null,
        quantity: form.quantity ? Number(form.quantity) : null, notes: form.notes || null,
      } } });
      onSaved?.();
    } catch (e) { setError(e.message); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change booking — {booking.businessName}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Required date" type="date" value={form.requiredDate || ''} onChange={set('requiredDate')} fullWidth size="small"
              InputLabelProps={{ shrink: true }} inputProps={{ min: today() }} />
            <TextField label={fieldsFor(booking.category).time} type="time" value={form.requiredTime || ''} onChange={set('requiredTime')} fullWidth size="small" InputLabelProps={{ shrink: true }} />
          </Stack>
          {fieldsFor(booking.category).quantity && (
            <TextField label={fieldsFor(booking.category).quantity} type="number" value={form.quantity || ''} onChange={set('quantity')} fullWidth size="small" inputProps={{ min: 1 }} />
          )}
          <TextField label="Notes for the vendor" value={form.notes || ''} onChange={set('notes')} fullWidth multiline rows={3} size="small" />
          {dateChanged && booking.status === 'confirmed' && (
            <Alert severity="warning">Changing the date sends the booking back to the vendor to confirm again.</Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!form.requiredDate || loading}>Save changes</Button>
      </DialogActions>
    </Dialog>
  );
}
